import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Link, useNavigate } from "react-router-dom"
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css"
import TableContainer from "../../components/Common/TableContainer"
import Moment from "moment"

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb"

import {
  addHealthdayFileUpload as onAddHealthdayFileUpload,
  addHealthdayDataUpload as onAddHealthdayDataUpload,
} from "store/actions"

//redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"

import {
  Badge,
  Button,
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from "reactstrap"

import Spinners from "components/Common/Spinner"
import { isEmpty } from "lodash"
import AlertContainer from "components/Common/AlertContainer"
import { CLEAR_HEALTHDAY_STATE } from "store/healthday/actionTypes"

function BulkUpload() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [file, setFile] = useState(null)
  const [error, setError] = useState("")
  const [resultData, setResultData] = useState({})

  const { result, loading } = useSelector(state => state.healthday)
  const [isLoading, setLoading] = useState(loading)
  const { basicSetting } = useSelector(state => state.Layout)

  document.title =
    "Bulk Upload | " + (basicSetting?.SiteName || process.env.REACT_APP_NAME)

  useEffect(() => {}, [dispatch])

  const handleSubmit = e => {
    e.preventDefault()
    if (!file) {
      setError("Required")
      return
    }
    setError("")
    dispatch(onAddHealthdayFileUpload(file))
  }

  useEffect(() => {
    if (!isEmpty(result)) {
      setResultData(result.ResponseData)
    }
  }, [result])

  const handleDownloadTemplate = () => {
    const link = document.createElement("a")
    link.href = `${process.env.REACT_APP_API_ROOT_URL}/_files/Template/digital_health_day_template.xlsx`
    link.setAttribute("download", "template.xlsx")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Bulk Upload"
            breadcrumbItem="Digital Health Days Bulk Upload"
          />
          {isLoading ? (
            <Spinners setLoading={setLoading} />
          ) : (
            <Row>
              <Col xs="12">
                <Card>
                  <CardBody>
                    <div hidden={!isEmpty(resultData)}>
                      <AlertContainer
                        message={result?.ErrorMessage}
                        color="warning"
                      />

                      <form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                          <Col xs="12" md="8">
                            <h5 className="mb-3">
                              The Excel file must be in the following format:
                            </h5>
                            <div className="fw-bold mb-3">
                              Timestamp, Centre, NDBAdded, FirstName, LastName,
                              DateOfBirth, Gender, Ethnicity, Address, Area,
                              Postcode, Mobile, ContactNumber, MobileNetwork,
                              MobileContract, MMPGroup, SmokeAlarm,
                              EmailAddress, NDBNumber, NDBNetwork, NDBRegistered
                              (comma-separated)
                            </div>
                            <ul>
                              <li>Header row should not be modified.</li>
                              <li>Worksheet name should not be changed.</li>
                              <li>
                                Centre, Gender, Ethnicity, DateOfBirth, Area,
                                Postcode are mandatory.
                              </li>
                              <li>
                                Use format: DD-MM-YYYY hh:mm:ss for Timestamp
                              </li>
                              <li>
                                Centre, Ethnicity, MobileContract and NDBNetwork
                                must exist in the system.
                              </li>
                            </ul>
                          </Col>
                          <Col
                            xs="12"
                            md="4"
                            className="text-md-end text-start mt-2 mt-md-0 d-flex align-items-start justify-content-md-end"
                          >
                            <Button
                              color="success"
                              onClick={handleDownloadTemplate}
                              className="btn-sm"
                            >
                              Download XLS Template
                            </Button>
                          </Col>
                        </Row>
                        <div className="mb-3" style={{ maxWidth: "400px" }}>
                          <Input
                            className="form-control"
                            type="file"
                            onChange={e => setFile(e.target.files[0])}
                          />
                          {error && (
                            <p style={{ color: "red", fontSize: "0.85rem" }}>
                              {error}
                            </p>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <Button color="primary" className="btn">
                            Proceed
                          </Button>
                        </div>
                      </form>
                    </div>
                    <div hidden={isEmpty(resultData)}>
                      <h5>{"Rejected Records - " + result?.RejectedCount}</h5>
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Timestamp</th>
                              <th>Centre</th>
                              <th>NDBAdded</th>
                              <th>FirstName</th>
                              <th>LastName</th>
                              <th>DateOfBirth</th>
                              <th>Gender</th>
                              <th>Ethnicity</th>
                              <th>Address</th>
                              <th>Area</th>
                              <th>Postcode</th>
                              <th>Mobile</th>
                              <th>ContactNumber</th>
                              <th>MobileNetwork</th>
                              <th>MobileContract</th>
                              <th>MMPGroup</th>
                              <th>SmokeAlarm</th>
                              <th>EmailAddress</th>
                              <th>NDBNumber</th>
                              <th>NDBNetwork</th>
                              <th>NDBRegistered</th>
                              <th>Result</th>
                              <th>Result Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultData && resultData?.length > 0 ? (
                              resultData.map((row, i) => (
                                <tr key={i}>
                                  <td>{row.Timestamp}</td>
                                  <td>{row.Centre}</td>
                                  <td>{row.NDBAdded}</td>
                                  <td>{row.FirstName}</td>
                                  <td>{row.LastName}</td>
                                  <td>
                                    {Moment(
                                      row.DateOfBirth,
                                      "DD-MM-YYYY"
                                    ).format("DD/MM/YYYY")}
                                  </td>
                                  <td>{row.Gender}</td>
                                  <td>{row.Ethnicity}</td>
                                  <td>{row.Address}</td>
                                  <td>{row.Area}</td>
                                  <td>{row.Postcode}</td>
                                  <td>{row.Mobile}</td>
                                  <td>{row.ContactNumber}</td>
                                  <td>{row.MobileNetwork}</td>
                                  <td>{row.MobileContract}</td>
                                  <td>{row.MMPGroup}</td>
                                  <td>{row.SmokeAlarm}</td>
                                  <td>{row.EmailAddress}</td>
                                  <td>{row.NDBNumber}</td>
                                  <td>{row.NDBNetwork}</td>
                                  <td>{row.NDBRegistered}</td>
                                  <td>{row.Result}</td>
                                  <td>{row.ResultNotes}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12" className="text-center">
                                  No records found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          color="primary"
                          className="btn"
                          onClick={x => {
                            dispatch(onAddHealthdayDataUpload())
                            dispatch({ type: CLEAR_HEALTHDAY_STATE })
                          }}
                          disabled={result?.RejectedCount > 0}
                        >
                          Proceed
                        </Button>
                        <Button
                          type="submit"
                          color="secondary"
                          onClick={x => {
                            setResultData({})
                            dispatch({ type: CLEAR_HEALTHDAY_STATE })
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

BulkUpload.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
}

export default BulkUpload
